import type { ScheduledEvent } from "@tih/shared";

export type EventRowPhase = "ongoing" | "upcoming" | "past";

export const DEFAULT_EVENT_DURATION_MS = 8 * 60 * 60 * 1000;

export function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

export function fromLocalInput(local: string): string {
  return new Date(local).toISOString();
}

export function defaultEndLocalFromStart(startLocal: string): string {
  if (!startLocal) return "";
  const end = new Date(startLocal);
  end.setTime(end.getTime() + DEFAULT_EVENT_DURATION_MS);
  return toLocalInput(end.toISOString());
}

export function rowPhase(
  ev: { startsAt: string; endsAt: string },
  now = Date.now(),
): EventRowPhase {
  const start = Date.parse(ev.startsAt);
  const end = Date.parse(ev.endsAt);
  if (now >= end) return "past";
  if (now >= start) return "ongoing";
  return "upcoming";
}

export function phaseBadgeClass(phase: EventRowPhase): string {
  if (phase === "ongoing") return "admin-badge admin-badge-live";
  if (phase === "upcoming") return "admin-badge admin-badge-upcoming";
  return "admin-badge admin-badge-muted";
}

export function phaseLabel(phase: EventRowPhase): string {
  if (phase === "ongoing") return "Ongoing";
  if (phase === "upcoming") return "Upcoming";
  return "Past";
}

function sortTier(phase: EventRowPhase): number {
  if (phase === "upcoming") return 0;
  if (phase === "ongoing") return 1;
  return 2;
}

export function compareAdminEvents(
  a: ScheduledEvent,
  b: ScheduledEvent,
  now: number,
): number {
  const pa = rowPhase(a, now);
  const pb = rowPhase(b, now);
  const tierDiff = sortTier(pa) - sortTier(pb);
  if (tierDiff !== 0) return tierDiff;
  if (pa === "past") {
    const byStart = Date.parse(b.startsAt) - Date.parse(a.startsAt);
    if (byStart !== 0) return byStart;
    return b.id - a.id;
  }
  return Date.parse(a.startsAt) - Date.parse(b.startsAt);
}

export function formatEventWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function eventDisplayTitle(ev: {
  title?: string | null;
  startsAt: string;
}): string {
  if (ev.title?.trim()) return ev.title.trim();
  return new Date(ev.startsAt).toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}
